import type { Component, Ref, ComputedRef } from 'vue';
import z from 'zod';

// Auth
export interface AuthData {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    refresh_expires_in: number;
    not_before_policy: number;
    session_state: string;
    scope: string;
}

// Support
export interface ServiceDesk {
    service_desk_id: number;
    name: string;
    hidden: boolean;
    jira_project_id: number;
    jira_project_key: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    request_types?: RequestType[];
    statuses: RequestTypeStatus[];
}

export interface RequestTypeStatus {
    name: string;
    category: string;
}

export interface LocationFootprint {
    locationType: string;
    locationId: string | number;
}

export interface RequestTypeGroup {
    request_type_group_id: number;
    name: string;
    service_desk_id: number;
    created_at: Date;
    modified_at: Date;
    created_by: string;
    modified_by: string;
    request_types: RequestType[];
    request_type_group_connections: RequestTypeGroupConnection;
}

export interface RequestTypeGroupConnection {
    request_type_group_id: number;
    request_type_id: number;
    created_at: Date;
    modified_at: Date;
    created_by: string;
    modified_by: string;
}

export interface RequestType {
    request_type_id: number;
    request_type_uuid: string;
    name: string;
    hidden: boolean;
    description: string;
    help_text: string;
    service_desk_id: number;
    service_desk_name: string;
    groups_count: number;
    created_at: Date;
    modified_at: Date;
    created_by: string;
    modified_by: string;
    // service_desk and request_type_group are returned by the legacy
    // organization-api endpoint but not by the new org-api (.NET) endpoint
    // for incident request types — see ITDEVELOP-2538.
    service_desk?: ServiceDesk;
    request_type_group?: RequestTypeGroup;
    // 0 = support, 1 = incident (per ITDEVELOP-2538).
    request_type_type?: number;
}

export const REQUEST_TYPE_TYPE_SUPPORT = 0;
export const REQUEST_TYPE_TYPE_INCIDENT = 1;

export interface Request {
    id: string;
    type: string;
    reference: string;
    summary: string;
    description: string;
    status: string;
    service_project: string;
    requester: string;
    response_time: number;
    created_at: Date;
    updated_at: Date;
}

export interface RequestFilter {
    serviceDesks: Option[];
    requestTypes: Option[];
    statuses: Option[];
    otherFilters: Option[];
    referenceSearch: string;
    summarySearch: string;
}

export interface RequestFilterData {
    serviceDeskId: number;
    requestTypes: string;
    statuses: string;
    showOrganizationRequests: boolean;
    createdByUser: boolean;
    userIsParticipant: boolean;
    referenceSearch?: string;
    summarySearch?: string;
}

export interface SupportRequestResponse {
    issues: RequestDetails[];
    maxResults: number;
    nextPageToken?: string;
    isLast: boolean;
    total?: number;
}

export interface RequestFieldResponse {
    requestTypeFields: RequestTypeField[];
    canRaiseOnBehalfOf: boolean;
    canAddRequestParticipants: boolean;
}

export interface RequestTypeField {
    fieldId: string;
    name: string;
    description: string;
    required: boolean;
    defaultValues: [
        {
            value: string;
            label: string;
            children: [
                {
                    value: string;
                    label: string;
                },
            ];
        },
    ];
    validValues: [
        {
            value: string;
            label: string;
            children: [
                {
                    value: string;
                    label: string;
                },
            ];
        },
    ];
    jiraSchema: {
        type: string;
        items: string;
        system: string;
        custom: string;
    };
    visible: boolean;
}

export interface requestFieldValue {
    fieldId: string;
    label: string;
    value: unknown;
}

export interface Breadcrumb {
    name: string;
    url: string;
}

export interface VisitorType {
    organization_visitor_type_id: number;
    visitor_type: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
}

export interface Visitor {
    organization_visitor_id: number;
    organization_visitor_type_id: number;
    organization_id: number;
    name: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    courses_count: number;
    visitor_type: VisitorType;
}

export interface ExtendedVisitor extends Omit<
    Visitor,
    | 'visitor_type'
    | 'organization_visitor_type_id'
    | 'organization_visitor_id'
    | 'organization_id'
    | 'created_at'
    | 'modified_at'
    | 'modified_by'
    | 'courses_count'
> {
    email: string;
    status: string;
    organizations: string[];
    id: string;
}

export interface VisitorByOrgId {
    ID: string;
    EMAIL: string;
    EMAIL_CONSTRAINT: string;
    EMAIL_VERIFIED: boolean;
    ENABLED: boolean;
    FEDERATION_LINK: null | string;
    FIRST_NAME: string;
    LAST_NAME: string;
    /** Only present on organization-scoped lists; the admin-wide list spans organizations. */
    company?: string | null;
    REALM_ID: string;
    USERNAME: string;
    CREATED_TIMESTAMP: number;
    SERVICE_ACCOUNT_CLIENT_LINK: null | string;
    NOT_BEFORE: boolean;
    security_briefed: 0 | 1;
    created_by: string | null;
    organizations?: number;
}

export interface VisitorOrganizationConnection {
    organization_id: number;
    name: string;
    service_level_id: number;
    description: string;
    created: boolean;
    /** The company THIS organization knows the visitor as — per connection, not per user. */
    company: string | null;
    /** Display name of the user who added the visitor to this organization. */
    added_by: string | null;
}

export interface VisitorById extends VisitorByOrgId {
    role: string;
    visitor_organizations: VisitorOrganizationConnection[];
}

export interface RequestDetails {
    expand: string;
    id: string;
    self: string;
    key: string;
    fields: {
        statuscategorychangedate: string;
        customfield_10070: unknown;
        customfield_10071: unknown;
        customfield_10072: unknown;
        customfield_10073: unknown;
        customfield_10074: unknown;
        fixVersions: unknown[];
        customfield_10110: unknown[];
        customfield_10111: unknown[];
        resolution: unknown;
        customfield_10112: unknown;
        customfield_10079: unknown;
        customfield_10113: unknown;
        customfield_10114: unknown;
        customfield_10104: unknown;
        customfield_10105: unknown;
        customfield_10106: unknown;
        customfield_10108: unknown;
        customfield_10109: unknown;
        lastViewed: unknown;
        customfield_10180: string | null;
        customfield_10060: unknown;
        customfield_10182: unknown;
        customfield_10061: unknown;
        customfield_10062: unknown;
        customfield_10183: unknown;
        customfield_10184: unknown;
        customfield_10063: unknown;
        customfield_10064: unknown;
        customfield_10185: unknown;
        customfield_10065: unknown;
        customfield_10066: unknown;
        customfield_11005: unknown;
        customfield_11875: { id?: string; value: string }[] | null; // label: Installation ticket (for delivery)
        priority: {
            self: string;
            iconUrl: string;
            name: string;
            id: string;
        };
        customfield_10100: unknown;
        customfield_10067: unknown;
        customfield_10068: unknown;
        customfield_10102: unknown;
        customfield_10069: unknown;
        customfield_10103: unknown;
        labels: unknown[];
        aggregatetimeoriginalestimate: unknown;
        timeestimate: unknown;
        versions: unknown[];
        issuelinks: unknown[];
        assignee: unknown;
        status: {
            self: string;
            description: string;
            iconUrl: string;
            name: string;
            id: string;
            statusCategory: {
                self: string;
                id: number;
                key: string;
                colorName: string;
                name: string;
            };
        };
        components: unknown[];
        customfield_10050: unknown;
        customfield_10051: unknown;
        customfield_10052: unknown;
        customfield_10173: unknown;
        customfield_10174: unknown;
        customfield_10053: unknown;
        customfield_10054: unknown;
        customfield_10175: unknown;
        customfield_10176: unknown;
        customfield_10055: unknown;
        customfield_10056: unknown;
        customfield_10177: unknown;
        customfield_10178: unknown;
        customfield_10057: unknown;
        customfield_10058: unknown;
        customfield_10179: unknown;
        customfield_10059: unknown;
        customfield_10049: unknown;
        aggregatetimeestimate: unknown;
        creator: {
            self: string;
            accountId: string;
            emailAddress: string;
            avatarUrls: {
                '48x48': string;
                '24x24': string;
                '16x16': string;
                '32x32': string;
            };
            displayName: string;
            active: boolean;
            timeZone: string;
            accountType: string;
        };
        subtasks: unknown[];
        customfield_10040: unknown;
        customfield_10041: unknown;
        customfield_10042: {
            languageCode: string;
            displayName: string;
        };
        reporter: {
            self: string;
            accountId: string;
            emailAddress: string;
            avatarUrls: {
                '48x48': string;
                '24x24': string;
                '16x16': string;
                '32x32': string;
            };
            displayName: string;
            active: boolean;
            timeZone: string;
            accountType: string;
        };
        aggregateprogress: {
            progress: number;
            total: number;
        };
        customfield_10046: unknown;
        customfield_10047: unknown;
        customfield_10048: unknown;
        customfield_10038: {
            id: string;
            name: string;
            _links: {
                self: string;
            };
            completedCycles: JiraSlaCycle[];
            ongoingCycle: {
                startTime: {
                    iso8601: string;
                    jira: string;
                    friendly: string;
                    epochMillis: number;
                };
                breachTime: {
                    iso8601: string;
                    jira: string;
                    friendly: string;
                    epochMillis: number;
                };
                breached: boolean;
                paused: boolean;
                withinCalendarHours: boolean;
                goalDuration: {
                    millis: number;
                    friendly: string;
                };
                elapsedTime: {
                    millis: number;
                    friendly: string;
                };
                remainingTime: {
                    millis: number;
                    friendly: string;
                };
            };
        };
        customfield_10039: {
            id: string;
            name: string;
            _links: {
                self: string;
            };
            completedCycles: JiraSlaCycle[];
            ongoingCycle: {
                startTime: {
                    iso8601: string;
                    jira: string;
                    friendly: string;
                    epochMillis: number;
                };
                breachTime: {
                    iso8601: string;
                    jira: string;
                    friendly: string;
                    epochMillis: number;
                };
                breached: boolean;
                paused: boolean;
                withinCalendarHours: boolean;
                goalDuration: {
                    millis: number;
                    friendly: string;
                };
                elapsedTime: {
                    millis: number;
                    friendly: string;
                };
                remainingTime: {
                    millis: number;
                    friendly: string;
                };
            };
        };
        progress: {
            progress: number;
            total: number;
        };
        votes: {
            self: string;
            votes: number;
            hasVoted: boolean;
        };
        worklog: {
            startAt: number;
            maxResults: number;
            total: number;
            worklogs: unknown[];
        };
        issuetype: {
            self: string;
            id: string;
            description: string;
            iconUrl: string;
            name: string;
            subtask: boolean;
            avatarId: number;
            hierarchyLevel: number;
        };
        timespent: unknown;
        customfield_10030: unknown;
        project: {
            self: string;
            id: string;
            key: string;
            name: string;
            projectTypeKey: string;
            simplified: boolean;
            avatarUrls: {
                '48x48': string;
                '24x24': string;
                '16x16': string;
                '32x32': string;
            };
        };
        customfield_10031: unknown;
        customfield_10032: unknown;
        customfield_10033: unknown;
        customfield_10034: unknown;
        aggregatetimespent: unknown;
        customfield_10035: unknown;
        customfield_10036: unknown;
        customfield_10157: unknown;
        customfield_10037: unknown;
        customfield_10027: unknown;
        customfield_10029: unknown;
        resolutiondate: unknown;
        workratio: number;
        issuerestriction: {
            issuerestrictions: Record<string, unknown>;
            shouldDisplay: boolean;
        };
        watches: {
            self: string;
            watchCount: number;
            isWatching: boolean;
        };
        created: string;
        customfield_10260: unknown;
        customfield_10329: unknown;
        customfield_10330: JiraAdfDocument | null;
        customfield_10315: JiraAdfDocument | null;
        customfield_10337: unknown;
        customfield_10336: string | null;
        customfield_10318: string | null;
        customfield_10508: { id?: string; value: string } | null;
        customfield_10140: unknown;
        customfield_10261: unknown;
        customfield_10020: unknown;
        customfield_10141: unknown;
        customfield_10262: unknown;
        customfield_10142: unknown;
        customfield_10263: unknown;
        customfield_10021: unknown;
        customfield_10264: unknown;
        customfield_10335: JiraAdfDocument | null;
        customfield_10143: unknown;
        customfield_10022: string;
        customfield_10144: unknown;
        customfield_10265: string | null;
        customfield_10023: unknown;
        customfield_10024: { accountId?: string; displayName: string }[];
        customfield_10145: unknown;
        customfield_10266: unknown;
        customfield_10146: unknown;
        customfield_10267: unknown;
        customfield_10025: unknown;
        customfield_10268: string;
        customfield_10026: unknown;
        customfield_10016: unknown;
        customfield_10574: unknown;
        customfield_10576: unknown;
        customfield_10575: unknown;
        customfield_10609: unknown;
        customfield_10610: unknown;
        customfield_10806: unknown;
        customfield_10807: unknown;
        customfield_10808: unknown;
        customfield_10809: unknown;
        customfield_10810: unknown;
        customfield_10811: unknown;
        customfield_10812: unknown;
        customfield_10838: unknown;
        customfield_10839: JiraAdfDocument | null;
        customfield_10843: unknown;
        customfield_10258: unknown;
        customfield_10259: unknown;
        customfield_10017: unknown;
        customfield_10138: unknown[];
        customfield_10018: {
            hasEpicLinkFieldDependency: boolean;
            showField: boolean;
            nonEditableReason: {
                reason: string;
                message: string;
            };
        };
        customfield_10139: unknown[];
        customfield_10019: string;
        updated: string;
        customfield_10093: unknown;
        customfield_10094: unknown;
        timeoriginalestimate: unknown;
        customfield_10095: unknown;
        customfield_10250: { id?: string; value: string }[] | null;
        customfield_10130: unknown;
        customfield_10097: unknown;
        description: {
            type: string;
            version: number;
            content: {
                type: string;
                content: {
                    type: string;
                    text: string;
                }[];
            }[];
        };
        customfield_10098: unknown;
        customfield_10010: {
            _links: {
                jiraRest: string;
                web: string;
                self: string;
                agent: string;
            };
            requestType: {
                _expands: string[];
                id: string;
                _links: {
                    self: string;
                };
                name: string;
                description: string;
                helpText: string;
                issueTypeId: string;
                serviceDeskId: string;
                portalId: string;
                groupIds: unknown[];
                icon: {
                    id: string;
                    _links: {
                        iconUrls: {
                            '48x48': string;
                            '24x24': string;
                            '16x16': string;
                            '32x32': string;
                        };
                    };
                };
            };
            currentStatus: {
                status: string;
                statusCategory: string;
                statusDate: {
                    iso8601: string;
                    jira: string;
                    friendly: string;
                    epochMillis: number;
                };
            };
        };
        customfield_10131: unknown[];
        customfield_10132: unknown;
        customfield_10254: { id?: string; value: string }[] | null;
        customfield_10134: unknown;
        customfield_10255: unknown;
        customfield_10014: unknown;
        customfield_10135: unknown;
        customfield_10256: unknown;
        timetracking: Record<string, unknown>;
        customfield_10257: unknown;
        customfield_10015: string | null;
        customfield_10126: unknown;
        customfield_10005: unknown;
        customfield_10006: unknown;
        customfield_10127: unknown;
        customfield_10007: unknown;
        security: unknown;
        customfield_10008: unknown;
        customfield_10129: unknown;
        attachment: {
            self: string;
            id: string;
            filename: string;
            author: {
                self: string;
                accountId: string;
                emailAddress: string;
                avatarUrls: {
                    '48x48': string;
                    '24x24': string;
                    '16x16': string;
                    '32x32': string;
                };
                displayName: string;
                active: boolean;
                timeZone: string;
                accountType: string;
            };
            created: string;
            size: number;
            mimeType: string;
            content: string;
            thumbnail?: string;
        }[];
        customfield_10009: unknown;
        summary: string;
        customfield_10080: unknown;
        customfield_10081: unknown[];
        customfield_10082: unknown;
        customfield_10083: unknown;
        customfield_10084: unknown;
        customfield_10085: unknown;
        customfield_10086: unknown;
        customfield_10240: unknown;
        customfield_10120: unknown;
        customfield_10000: string;
        customfield_10121: unknown;
        customfield_10001: unknown;
        customfield_10122: unknown[];
        customfield_10002: {
            id: string;
            name: string;
            created: {
                iso8601: string;
                jira: string;
                friendly: string;
                epochMillis: number;
            };
            _links: {
                self: string;
            };
        }[];
        customfield_10123: unknown;
        customfield_10244: string | null;
        customfield_10124: unknown;
        customfield_10003: unknown;
        customfield_10115: unknown;
        customfield_10117: unknown;
        environment: unknown;
        customfield_10118: unknown;
        customfield_10119: string | null;
        duedate: unknown;
        comment: {
            comments: {
                self: string;
                id: string;
                author: {
                    self: string;
                    accountId: string;
                    emailAddress: string;
                    avatarUrls: {
                        '48x48': string;
                        '24x24': string;
                        '16x16': string;
                        '32x32': string;
                    };
                    displayName: string;
                    active: boolean;
                    timeZone: string;
                    accountType: string;
                };
                body: {
                    type: string;
                    version: number;
                    content: {
                        type: string;
                        content: {
                            type: string;
                            text: string;
                        }[];
                    }[];
                };
                updateAuthor: {
                    self: string;
                    accountId: string;
                    emailAddress: string;
                    avatarUrls: {
                        '48x48': string;
                        '24x24': string;
                        '16x16': string;
                        '32x32': string;
                    };
                    displayName: string;
                    active: boolean;
                    timeZone: string;
                    accountType: string;
                };
                created: string;
                updated: string;
                jsdPublic: boolean;
            }[];
            self: string;
            maxResults: number;
            total: number;
            startAt: number;
        };
    };
    comments: {
        _expands: string[];
        size: number;
        start: number;
        limit: number;
        isLastPage: boolean;
        _links: {
            self: string;
            base: string;
            context: string;
        };
        values: {
            _expands: string[];
            id: string;
            body: string;
            public: boolean;
            author: {
                accountId: string;
                emailAddress: string;
                displayName: string;
                active: boolean;
                timeZone: string;
                _links: {
                    jiraRest: string;
                    avatarUrls: {
                        '48x48': string;
                        '24x24': string;
                        '16x16': string;
                        '32x32': string;
                    };
                    self: string;
                };
            };
            created: {
                iso8601: string;
                jira: string;
                friendly: string;
                epochMillis: number;
            };
            _links: {
                self: string;
            };
            // Only present on the comment returned by POST — list of issue-level attachments
            // newly added by this comment, in `fields.attachment` shape, for local UI sync.
            newDirectAttachments?: RequestDetails['fields']['attachment'];
            attachments: {
                size: number;
                start: number;
                limit: number;
                isLastPage: boolean;
                _links: {
                    self: string;
                    base: string;
                    context: string;
                };
                values: {
                    filename: string;
                    author: {
                        accountId: string;
                        emailAddress: string;
                        displayName: string;
                        active: boolean;
                        timeZone: string;
                        _links: {
                            jiraRest: string;
                            avatarUrls: {
                                '48x48': string;
                                '24x24': string;
                                '16x16': string;
                                '32x32': string;
                            };
                            self: string;
                        };
                    };
                    created: {
                        iso8601: string;
                        jira: string;
                        friendly: string;
                        epochMillis: number;
                    };
                    size: number;
                    mimeType: string;
                    _links: {
                        jiraRest: string;
                        content: string;
                        thumbnail?: string;
                    };
                }[];
            };
        }[];
    };
}
/** Atlassian Document Format node, as Jira returns rich-text custom fields. */
export interface JiraAdfNode {
    type: string;
    text?: string;
    attrs?: Record<string, unknown>;
    content?: JiraAdfNode[];
}

export interface JiraAdfDocument {
    type: 'doc';
    version: number;
    content: JiraAdfNode[];
}

/** One finished SLA cycle on a Jira Service Management request (the shape of `completedCycles[]`). */
export interface JiraSlaCycle {
    startTime: { iso8601: string; jira: string; friendly: string; epochMillis: number };
    stopTime: { iso8601: string; jira: string; friendly: string; epochMillis: number };
    breachTime?: { iso8601: string; jira: string; friendly: string; epochMillis: number };
    breached: boolean;
    goalDuration: { millis: number; friendly: string };
    elapsedTime: { millis: number; friendly: string };
    remainingTime: { millis: number; friendly: string };
}

export interface RequestComment {
    self: string;
    id: string;
    author: {
        self: string;
        accountId: string;
        emailAddress: string;
        avatarUrls: {
            '48x48': string;
            '24x24': string;
            '16x16': string;
            '32x32': string;
        };
        displayName: string;
        active: boolean;
        timeZone: string;
        accountType: string;
    };
    body: {
        version: number;
        type: string;
        content: {
            type: string;
            content: {
                type: string;
                text: string;
            }[];
        }[];
    };
    updateAuthor: {
        self: string;
        accountId: string;
        emailAddress: string;
        avatarUrls: {
            '48x48': string;
            '24x24': string;
            '16x16': string;
            '32x32': string;
        };
        displayName: string;
        active: boolean;
        timeZone: string;
        accountType: string;
    };
    created: string;
    updated: string;
    jsdPublic: boolean;
}

// Admin
export interface AdminOrganizationData {
    created_by: string;
    modified_at: number;
    modified_by: string;
    name: string;
    description?: string;
    organization_id: number;
    organization_type_id: string;
    organization_type: OrganizationType;
    service_level_id: number;
    devices_count: number;
    users_count: number;
}

export interface AdminAccessCard {
    accessCardId: string;
    cardOwnerName: string;
    userId: string | null;
    organizationNames: string;
    siteName: string;
    ticketKey: string | null;
    requestedByName: string;
    startDate?: number | null;
    endDate?: number | null;
    status: string;
    accessCardType: string;
}

export interface AdminOrgByUserData extends AdminOrganizationData {
    contacts: AdminContact[];
    org_role: string;
}

export interface AdminUserOrgData extends AdminOrganizationData {
    org_role: string;
}

export interface AdminUserById {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    system_roles: string[];
    enabled: boolean;
}

export interface AdminOrganizationUserData {
    ID: string;
    EMAIL: string;
    EMAIL_CONSTRAINT: string;
    EMAIL_VERIFIED: boolean;
    ENABLED: boolean;
    FEDERATION_LINK: null | string;
    FIRST_NAME: null | string;
    LAST_NAME: null | string;
    REALM_ID: string;
    USERNAME: string;
    CREATED_TIMESTAMP: number;
    SERVICE_ACCOUNT_CLIENT_LINK: null | string;
    NOT_BEFORE: boolean;
    PHONE_NUMBER: string;
    'org-admin': number;
}

export interface AdminOrganizationByIdData {
    organization_id: number;
    organization_uuid: string;
    organization_type_id: string;
    name: string;
    description: string;
    service_level_id: number;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    users_count: number;
    visitors_count: number;
    roles_count: number;
    accessrequests_count: number;
}

export interface AdminOrganizationVisitorData {
    organization_visitor_id: number;
    organization_visitor_type_id: number;
    organization_id: number;
    name: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    visitor_type_name: string;
}

export interface AdminOrganizationAccessRequest {
    site_access_request_id: number;
    organization_id: number;
    site_id: string;
    ticket_reference: string;
    estimated_arrival: number;
    estimated_departure: number;
    confirmed_arrival: unknown;
    confirmed_departure: unknown;
    request_status: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
}

export interface AdminServiceLevelOption {
    service_level_id: number;
    name: string;
    description: string;
}

export interface AdminOrgInfoBody {
    locations: Array<{
        locationType: string;
        locationId: string | number;
    }>;
    service_desks: Array<{
        service_desk_id: number;
        request_types: Array<{
            request_type_id: number;
        }>;
    }>;
    organization_creation_form: {
        service_level_id?: number;
        organization_name?: string;
        organization_type_id?: string;
        description?: string;
        contact_first_name?: string;
        contact_last_name?: string;
        contact_email?: string;
        contact_phone?: string;
    };
}

export interface AdminServiceDeskInfo extends Omit<ServiceDesk, 'statuses'> {
    request_types: RequestType[];
}

export interface AdminServiceDeskInfoEvent {
    value: AdminServiceDeskInfo;
}

export interface AdminServiceDeskData extends AdminServiceDeskInfo {
    request_types: (RequestType & { isChecked: boolean })[];
    isExpanded: boolean;
    isChecked: boolean;
}

export interface AdminServiceDeskReadOnly {
    id: number;
    name: string;
    request_types: {
        id: number;
        name: string;
    }[];
}

export interface AdminOrganizationDefaultGroup {
    default_group_id: number;
    name: string;
    description: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    sites: Site[];
    regions: Region[];
    request_types: RequestType[];
    organization_roles: Role[];
}

export interface AdminOrganizationFormData {
    organization_creation_form_id: number;
    organization_id: number;
    organization_type_id: string;
    status: 'IN PROGRESS' | 'WAITING FOR APPROVAL' | 'APPROVED';
    next_step: 'Contract' | 'Role Pool' | 'Default Groups' | 'Summary' | 'Review' | '';
    contact_first_name: string;
    contact_last_name: string;
    contact_email: string;
    contact_phone: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    organization: AdminOrganizationData;
}

export interface SortableAdminOrganizationFormData extends AdminOrganizationFormData {
    sort_name: string;
    sort_admin: 'CIDM' | 'NOC';
}

export interface AdminSLATag {
    sla_tag_id: string;
    name: string;
    created_at?: number;
    modified_at?: number;
    created_by?: string;
    modified_by?: string;
}
export interface AdminSLAType {
    sla_type_id: string;
    name: string;
    unit: 'Hours' | '%' | 'C' | 'Days';
    created_at?: number;
    modified_at?: number;
    created_by?: string;
    modified_by?: string;
    sla_tags: AdminSLATag[];
}

export interface AdminSLABody {
    start_date: number;
    end_date: number;
    description: string;
    sla: {
        sla_type_id: string;
        sla_tag_id?: string | undefined;
        value: string;
    }[];
}

export interface AdminContract {
    contract_id?: string;
    description: string;
    organization_id: string;
    sla: Array<{
        sla_type: {
            sla_type_id: string;
            name: string;
            unit: 'C' | '%' | 'Hours' | 'Days';
        };
        sla_tags: Array<{
            sla_tag_id: string;
            name: string;
            value: string;
        }>;
        value: string;
    }>;
}

// Admin User management
export interface AdminUserEstablished {
    access: {
        manage: boolean;
        impersonate: boolean;
        map_roles: boolean;
        view: boolean;
        manage_group_membership: boolean;
    };
    not_before: number;
    totp: boolean;
    enabled: boolean;
    created_timestamp: number;
    attributes: {
        account_status: Array<string>;
    };
    email_verified: boolean;
    email?: string;
    first_name?: string;
    last_name?: string;
    username: string;
    id: string;
    role: string;
    organization_count: number;
    phone_number?: string;
}

export interface AdminUserImpersonationUser {
    id: string;
    email?: string;
    username?: string;
    firstName?: string;
    lastName?: string;
}

export interface AdminUserImpersonationRequest {
    userImpersonationId: string;
    subject: AdminUserImpersonationUser;
    requestor: AdminUserImpersonationUser;
    description: string;
    startDate: string;
    endDate: string;
    action: 'read' | 'write';
    status: 'pending' | 'approved' | 'declined';
}

export interface AdminSubjectAccessToken {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    refreshExpiresIn: number;
    tokenType: string;
    scope: string;
}

/** An organization group or role selected at invite time, resolved to its current name. */
export interface InvitationSelection {
    id: number;
    /** Null when the group/role has been deleted since the invitation was created. */
    name: string | null;
}

export interface AdminUserInvitation {
    user_invitation_id: number;
    email: string;
    first_name: string;
    last_name: string;
    organization: Pick<OrganizationData, 'organization_id' | 'name'> | null;
    status: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    approver?: string | null;
    user?: string | null;
    creator?: Pick<User, 'ID' | 'FIRST_NAME' | 'LAST_NAME' | 'EMAIL' | 'ENABLED' | 'USERNAME'> | null;
    groups: InvitationSelection[];
    roles: InvitationSelection[];
}

export interface OrganizationUserInvitation {
    user_invitation_id: number;
    email: string;
    first_name: string;
    last_name: string;
    organization_id: number;
    organization?: Pick<OrganizationData, 'organization_id' | 'name'>;
    status: string;
    created_at: Date;
    modified_at: Date;
    created_by: string | null;
    creator?: Pick<User, 'ID' | 'FIRST_NAME' | 'LAST_NAME' | 'EMAIL' | 'ENABLED' | 'USERNAME'> | null;
    modified_by: string | null;
    groups: InvitationSelection[];
    roles: InvitationSelection[];
}

/**
 * Wire shape of the .NET org-api invitation DTO (camelCase), mapped in the stores to the
 * snake_case view models above.
 */
export interface DotNetUserInvitationDto {
    userInvitationId: number;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    organizationId: number | null;
    organizationName: string | null;
    status: string;
    createdAt: number;
    modifiedAt: number;
    createdBy: string | null;
    creator?: {
        id: string;
        firstName: string | null;
        lastName: string | null;
        email: string | null;
        username: string | null;
        enabled: boolean;
    } | null;
    comment?: string | null;
    groups?: InvitationSelection[];
    roles?: InvitationSelection[];
}

// Admin Contact Management

// ITDEVELOP-1775: a single contact-type assignment for a user. Contacts with
// multiple types expose one of these per type in the `contact_types` array,
// while retaining each assignment's `organization_contact_id` so an individual
// type can still be deleted (the delete endpoints are row-centric).
export interface ContactTypeAssignment {
    contact_type: 'Billing' | 'Technical' | 'Primary';
    organization_contact_id: number;
    organization_contact_type_id: number | null;
}

export interface AdminContact {
    organization_id: number;
    user_id: string;
    // ITDEVELOP-1775: contacts are grouped per user; assigned types are listed here.
    contact_types?: ContactTypeAssignment[];
    // Per-type fields are only present on the legacy un-grouped responses
    // (e.g. AdminOrgByUserData.contacts) and are optional on the grouped shape.
    organization_contact_id?: number;
    organization_contact_type_id?: number;
    created_at?: number;
    modified_at?: number;
    created_by?: string | null;
    modified_by?: string | null;
    contact_role?: {
        organization_contact_type_id: number;
        contact_type: 'Billing' | 'Technical' | 'Primary';
        created_at: number;
        modified_at: number;
        created_by: string | null;
        modified_by: string | null;
    } | null;
    contact_organization: {
        organization_id: number;
        name: string;
        service_level_id: number;
        description: string | null;
        created: boolean;
        created_at: number;
        modified_at: number;
        created_by: string | null;
        modified_by: string | null;
    };
    user: {
        user_id: string;
        first_name: string | undefined;
        last_name: string | undefined;
        email: string | undefined;
        phone: string | undefined;
        system_roles: string[];
    };
}

export interface Contact {
    organization_id: number;
    user_id: string;
    // ITDEVELOP-1775: contacts are grouped per user; assigned types are listed here.
    contact_types?: ContactTypeAssignment[];
    // Per-type fields are only present on legacy un-grouped responses.
    organization_contact_id?: number;
    organization_contact_type_id?: number;
    created_at?: number;
    modified_at?: number;
    created_by?: string | null;
    modified_by?: string | null;
    contact_role?: ContactType;
    user: ContactUser;
}

export interface ContactType {
    organization_contact_type_id: number;
    contact_type: 'Billing' | 'Technical' | 'Primary';
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    organizations_count?: number;
    users_count?: number;
}

export interface ContactUser {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    system_roles: [string];
}

export interface AdminContactClientFormData {
    checked?: boolean;
    org_id: number;
    org_name: string;
    user_id: string;
    user_name: string;
    user_email: string;
    contact_type_ids: number[];
}

export interface AdminContactFormData {
    contact_types: {
        organization_contact_type_id: number;
    }[];
    user_id: string;
    organization_id: number;
}

// Notificatinos

export interface Notification {
    id: string;
    title: string;
    summary: string;
    content: string;
    type: string;
    startDate: Date;
    endDate?: Date;
    notificationDate: Date;
    severity: NOTIFICATION_SEVERITY;
    creator: User;
    fields?: NotificationTemplateField[];
    template?: NotificationTemplate;
    acknowledged?: 0 | 1 | null;
    notification_template_id: string;
    notification_type_id: string;
    notification_type: NotificationType;
    parent_id: string | null;
}
export interface NotificationGroup {
    [key: string]: Notification[];
}

export interface NotificationType {
    notification_type_id: string;
    name: string;
    description: string;
    locational: boolean;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
}

export enum NOTIFICATION_SEVERITY {
    LOW = 1,
    MEDIUM = 2,
    HIGH = 3,
    CRITICAL = 4,
}

export interface NotificationResponse {
    notification_id: string;
    parent_id: string | null;
    notification_template_id: string;
    notification_type_id: string;
    start_date: number;
    end_date: number;
    send_date: number;
    title: string;
    severity: NOTIFICATION_SEVERITY;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    acknowledged?: 1 | 0 | null;
    creator: User;
    notification_type: NotificationType;
    notification_template: NotificationTemplate;
}

export type AllNotificationResponse = NotificationResponse;

export interface NotificationResponseById extends NotificationResponse {
    notification_fields: NotificationTemplateField[];
}

export interface NotificationThreadResponseById extends NotificationResponse {
    notification_fields: NotificationTemplateField[];
    notification_template: NotificationTemplate;
}

export interface NotificationThread extends Notification {
    templateSummary: string;
}

export interface NotificationPreferences {
    title: string;
    start_date?: Date;
    end_date?: Date;
    severity: string;
    type: string;
    template_id: string;
    template_fields: { key: string; value: string; type: string }[];
    locations: { location_id: string }[];
}

export interface NotificationFormData {
    notification: {
        parent_id?: string;
        notification_type_id: string;
        title: string;
        notification_template_id: string;
        notification_fields: { key: string; value: string }[];
        start_date: number;
        end_date?: number;
        send_date?: number;
        severity: number;
    };
    locations: { location_id: string }[];
}

export interface NotificationTemplate {
    notification_template_id: string;
    notification_type_id: string;
    name: string;
    title: string;
    summary: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    template_fields: NotificationTemplateField[];
}

export interface NotificationTemplateField {
    notifcation_template_id: string;
    field_key: string;
    field_type: 'textarea' | 'input' | 'richtext';
    field_label: string;
    field_value: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
}
export interface NotificationFilter {
    type: string;
    values: { type: string; value: number | string; name: string; selected: boolean }[];
}

export interface NotificationSubscriptionResponse {
    notification_name: string;
    notification_type_id: string;
    locational: number;
    email_severity: number;
    sms_severity: number;
    user_id: string;
    subscribed: number;
    acknowledged: 0 | 1 | null;
}

export interface SubscriptionFormBody {
    notification_type_id: string;
    email_severity: number;
    sms_severity: number;
}

export interface OrganizationByLocation {
    organization_id: number;
    name: string;
}

// Organization
export interface OrganizationData {
    organization_id: number;
    organization_uuid: string;
    name: string;
    service_level_id?: number | null;
    created_at: Date;
    created_by: string;
    modified_at: Date;
    modified_by: string;
    organization_roles: [
        {
            name: string;
            organization_role_id: number;
        },
    ];
    regions: Region[];
}

export interface User {
    ID: string;
    USERNAME: string;
    FIRST_NAME: string;
    LAST_NAME: string;
    EMAIL: string;
    EMAIL_CONSTRAINT: string;
    EMAIL_VERIFIED: boolean;
    CREATED_AT: number;
    ENABLED: boolean;
    FEDERATION_LINK: string;
    NOT_BEFORE: boolean;
    REALM_ID: string;
    SERVICE_ACCOUNT_CLIENT_LINK: string;
    PHONE_NUMBER: string;
    SYSTEM_ROLE: string;
    roles_count: number;
    locations_count: number;
    regions_count: number;
    sites_count: number;
    buildings_count: number;
    rooms_count: number;
    pods_count: number;
    groups_count: number;
    courses_count: number;
    organizations: OrganizationData[];
    user_id: string;
}

export interface UserDTO {
    id: string;
    emailVerified: boolean;
    enabled: boolean;
    firstName: string;
    lastName: string;
    username: string;
    created: string;
    rolesCount: number;
    groupsCount: number;
    highestRole: string;
}

export interface UserWithContacts extends Omit<User, 'organizations'> {
    contact: Omit<AdminContact, 'user' | 'contact_organization'>[];
}

export interface Role {
    organization_role_id: number;
    name: string;
    description: string | null;
    service_level_id: number;
    service_level_name?: string;
    users_count: number;
    groups_count: number;
    organization_role_connection_id: number;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    service_level?: ServiceLevel;
}

export interface ServiceLevel {
    service_level_id: number;
    name: string;
    description: string;
    created_at: number | null;
    modified_at: number | null;
    created_by: string | null;
    modified_by: string | null;
}

export interface SimpleRole {
    name: string;
    organization_role_id: number;
}

export interface OrganizationContactFormData {
    organization_contact_type_id: number;
    user_id: string;
}

export interface ConnectionRole {
    organization_role_connection_id: number;
    organization_id: number;
    organization_role_id: number;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    organization_role: Role;
    users: User[];
    request_types: RequestType[];
    request_type_groups: RequestTypeGroup[];
    organization_groups: Group[];
}

export interface Group {
    organization_group_id: number;
    organization_id: number;
    organization_group_uuid: string;
    name: string;
    description: string;
    created_at: number;
    modified_at: number;
    created_by: string;
    modified_by: string;
    users_count: number;
    roles_count: number;
    requesttypes_count: number;
    regions_count: number;
    sites_count: number;
    buildings_count: number;
    rooms_count: number;
    pods_count: number;
    users: User[];
    organization_roles: Role[];
    request_types: RequestType[];
    reqions: Region[];
    sites: Site[];
    buildings: Building[];
    rooms: Room[];
    pods: Pod[];
}

export interface Region {
    region_id: number;
    location_id: number;
    location_type: string;
    name: string;
    initials: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    parent_name: string;
    sites: Site[];
    buildings: Building[];
    groups_count: number;
    users_count: number;
    hidden: boolean;
}

export interface Site {
    site_id: number;
    location_id: number;
    location_type: string;
    name: string;
    street_address: string;
    postal_code: string;
    city: string;
    country: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    parent_name: string;
    parent_id: number;
    buildings: Building[];
    groups_count: number;
    users_count: number;
    hidden: boolean;
}

export interface Building {
    building_id: number;
    location_id: number;
    location_type: string;
    name: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    parent_name: string;
    parent_id: number;
    rooms: Room[];
    floors: Floor[];
    groups_count: number;
    users_count: number;
    hidden: boolean;
}

export interface Room {
    room_id: number;
    location_id: number;
    location_type: string;
    name: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    parent_name: string;
    parent_id: number;
    groups_count: number;
    users_count: number;
    pods: Pod[];
    hidden: boolean;
}

export interface Pod {
    pod_id: number;
    location_id: number;
    location_type: string;
    name: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    parent_name: string;
    parent_id: number;
    groups_count: number;
    users_count: number;
    hidden: boolean;
    rack_rows: RackRow[];
}

export interface RackRow {
    rack_row_id: number;
    location_id: number;
    location_type: string;
    name: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    parent_name: string;
    parent_id: number;
    groups_count: number;
    users_count: number;
    hidden: boolean;
    racks: Rack[];
}

export interface Rack {
    rack_id: number;
    location_id: number;
    location_type: string;
    name: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    parent_name: string;
    parent_id: number;
    groups_count: number;
    users_count: number;
    hidden: boolean;
}

export interface Floor {
    floor_id: number;
    location_id: number;
    location_type: string;
    name: string;
    building_id: number;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    rooms: Room[];
    groups_count: number;
    users_count: number;
    hidden: boolean;
}

export type Location = Region | Site | Floor | Building | Room | Pod | RackRow | Rack;

export interface UUIDLocation {
    id: string;
    name: string;
    locationType: string;
    parentId: string | null;
    children: UUIDLocation[];
}

export interface OldUUIDLocation {
    location_id: string;
    name: string;
    location_type: string;
    old_id: number;
    parent_id: string | null;
    children: OldUUIDLocation[];
}

// helpers
export interface Tab {
    id: number;
    name: string;
    /** Button text; defaults to `name` with its first letter capitalised. */
    label?: string;
    /** One-liner shown under the tab strip while this tab is active. */
    description?: string;
    component: Component;
    props?: object;
    associationCount?: number | ComputedRef<number | undefined>;
    data?: unknown;
    loading?: boolean | Ref<boolean> | ComputedRef<boolean>;
}

export interface UserForm {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    enabled: boolean;
}

export interface GroupForm {
    name: string;
    description: string;
}

export type ToastType = 'check' | 'warning' | 'error' | 'info';

export interface Toast {
    message: string;
    type: ToastType;
    index: number;
}

export interface GenericInfoCard {
    sensorId: string;
    sensorName: string;
    time: string;
    value: number;
    building: string;
    group: string;
    unit?: string;
    info?: string;
}

export const OptionSchema = z.object({
    // Option values are ids, enum strings and whole objects depending on the select; narrowing this
    // ripples into ~15 components and is its own pass.
    value: z.any(),
    label: z.string(),
    dropdownClass: z.string().optional(),
    placeholderClass: z.string().optional(),
    disabled: z.boolean().optional(),
});

export type Option = z.infer<typeof OptionSchema>;

export interface Course {
    course_id: string;
    title: string;
    description: string;
    duration: number;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    sections: Section[];
}

export interface SimpleCourse {
    course_id: string;
    title: string;
    description: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
}

export interface Section {
    section_id: string;
    course_id: string;
    duration: number;
    title: string;
    order: number | null;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    contents: Content[];
    quizes: Quiz[];
}

export interface Content {
    content_id: string;
    section_id: string;
    content_type_id: string;
    title: string;
    description: string;
    duration: number;
    order: number | null;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    type: ContentType;
    images: Image[];
    videos: Video[];
    paragraphs: Paragraph[];
}

export interface ContentType {
    content_type_id: string;
    name: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
}

export interface Image {
    content_image_id: string;
    content_id: string;
    file_id: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
}

export interface Video {
    content_video_id: string;
    content_id: string;
    file_id: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
}

export interface Paragraph {
    content_paragraph_id: string;
    content_id: string;
    paragraph: string;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
}

export interface Quiz {
    quiz_id: string;
    section_id: string;
    title: string;
    description: string;
    order: number | null;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    questions: Question[];
}

export interface Question {
    question_id: string;
    quiz_id: string;
    title: string;
    description: string;
    order: number;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
    options: QuestionOption[];
}

export interface QuestionOption {
    question_option_id: string;
    question_id: string;
    question: string;
    correct: boolean;
    created_at: number;
    modified_at: number;
    created_by: string | null;
    modified_by: string | null;
}

export interface NestedOption extends Option {
    children: Option[];
}

export interface SimpleUser {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface ReportOverviewResponse {
    requestTypeName: string;
    count: number;
    pagesize: number;
    offset: number;
    totalCount: number;
    statusCode: number;
    data: ReportOverviewEntry[];
}
export interface ReportOverviewEntry {
    requestTypeReportId: string;
    requestTypeId: number;
    organizationId: number;
    requestCount: number;
    startDate: string;
    endDate: string;
    createdAt: string;
    modifiedAt: string;
    createdBy: string;
    modifiedBy: string;
}

export interface RequestTypeReport {
    requestTypeReportId: string;
    requestTypeId: number;
    requestTypeName: string;
    organizationId: string;
    averageResolveTime: number;
    totalResolutionTime: number;
    averageResolutionSuccessRate: number;
    requestCount: number;
    startDate: string;
    endDate: string;
    createdAt: string;
    count: number;
    pagesize: number;
    offset: number;
    totalCount: number;
    statusCode: number;
    data: ReportEntry[];
}

export interface ReportEntry {
    poNumber: string;
    ticketId: string;
    resolutionTimeSuccess: boolean;
    resolvedTime: number;
    resolutionDate: string;
    status: string;
    createdAt: string; // ISO date string
    modifiedAt: string; // ISO date string (can be invalid/placeholder)
    createdBy: string;
}

export interface OrganizationType {
    organization_type_id: string;
    name: string;
    default_system_role: string;
    default_system_group: string;
}

export interface AccessCard {
    access_card_id: string;
    access_card_physical_id: string | null;
    access_card_type: string;
    created_by: string;
    start_date: number;
    end_date: number | null;
    created_at: number;
    creator: string;
    site_name: string;
    status: string;
    ticket_id: number;
    ticket_key: string;
    user: string;
    user_id: string;
}

export interface CrossConnectionsOverviewResponse {
    count: number;
    pagesize: number;
    totalCount: number;
    offset: number;
    statusCode: number;
    data: CrossConnectionsOverview[];
}

export interface CrossConnectionsOverview {
    tenant: string;
    terminationsA: Termination[];
    terminationsB: Termination[];
    id: number;
}

export interface Termination {
    objectId: number;
    device: Device;
    id: number;
    name: string;
}

export interface Device {
    status: string;
    location: {
        id: number;
        name: string;
    };
    rack: {
        id: number;
        name: string;
    };
    id: number;
    name: string;
}

export interface BMSDataResponse {
    count: number;
    pagesize: number;
    offset: number;
    totalCount: number;
    statusCode: number;
    data: BMSData[];
}

export interface RawBMSDataResponse {
    count: number;
    pagesize: number;
    offset: number;
    totalCount: number;
    statusCode: number;
    data: RawBMSData[];
}
export interface RawBMSData {
    sensorId: string;
    sensorName: string;
    time: string;
    startTime: string;
    stopTime: string;
    value: number;
    field: string;
    measurement: string;
    building: string;
    group: string;
    host: string;
    site: string;
    topic: string;
    tag: string;
    unit: string;
}

export interface BMSDataResponse {
    count: number;
    pagesize: number;
    totalCount: number;
    offset: number;
    data: BMSData[];
    status: string;
}
export interface BMSData {
    time: string;
    value: number;
    group: string;
}

export interface graphData {
    locationName: string;
    data: BMSData[];
}

export interface ExportObject {
    exportType: 'csv' | 'json';
    fields: unknown[] | Record<string, unknown>[];
    downloadName: string;
    CSVHeaders?: string[];
}

export interface ExportInfo {
    exportType: { label: string; value: string };
    dataPointsCount: number;
    interval: number;
    intervalUnit: { label: string; value: string }[];
    startDate: Date;
    endDate: Date;
    aggregate: { label: string; value: string }[];
}

export interface DEBUG_IncidentCatalog {
    categories: {
        id: string; // TODO: get the actual schema
        name: string; // TODO: get the actual schema
        description: string; // TODO: get the actual schema
        types: DEBUG_IncidentCatalogType[]; // TODO: get the actual schema
    }[];
}

export interface DEBUG_IncidentCatalogType {
    id: string; // TODO: get the actual schema
    name: string; // TODO: get the actual schema
    description: string; // TODO: get the actual schema
    categoryId: string; // TODO: get the actual schema
}
